import React from 'react';
import PropTypes from 'prop-types';

import { useMutation } from '@sopranodesign/pulse-hooks';
import {
  Flex,
  Button,
  Divider,
  useNotification
} from '@sopranodesign/pulse-components';
import {
  useConditionalReducer,
  Conditionals,
  AddConditionalButton,
  ConditionalContext,
  Match
} from '@sopranodesign/pulse-conditionals';

import Modal from '../../../Modal';
import { conditionals } from '../../../../../../subjects/index';
import { useBuilder, useStep } from '../../../../hooks/index';
import { updateStepTransition } from '../../../../api/index';
import TransitionPreview from '../Transition/TransitionPreview';

const EditValidationNode = ({ handleClose, node }) => {
  const { dispatch } = useBuilder();

  const start = useStep(node.base.start.id);

  const destination = useStep(node.base.destination.id);

  const conditional = useConditionalReducer({
    subjects: conditionals,
    conditionals: {
      conditionals: node.base.conditionals,
      match: node.base.match
    }
  });

  const { showSuccess } = useNotification();

  const { isPending, mutate } = useMutation({
    mutationFn: updateStepTransition,
    onSuccess: ({ data }) => {
      showSuccess('Successfully updated validation');
      dispatch({
        type: 'UPDATE_TRANSITION',
        stepId: node.meta.stepId,
        transitionId: node.id,
        transitionDetails: data.data,
        transitionType: 'validation'
      });
      handleClose();
    }
  });

  const handleSubmit = event => {
    event.preventDefault();

    if (conditional.valid && !isPending) {
      mutate({
        stepId: node.meta.stepId,
        transitionId: node.id,
        transition: {
          description: `Validation for step #${node.meta.stepId}`,
          transition: {
            id: destination.id,
            ...conditional.form,
            variables: node.base.variables.map(variable => variable.id),
            metrics: node.base.metrics.map(metric => metric.id)
          }
        }
      });
    }
  };

  const startNode = {
    id: start.id,
    blocks: [],
    base: start
  };

  const destinationNode = {
    id: destination.id,
    blocks: [],
    base: { ...destination, responses: node.base.responses }
  };

  return (
    <Modal
      header="Edit validation"
      handleClose={handleClose}
      style={{ maxWidth: '100%', width: '80rem' }}>
      <Flex>
        <Flex borderRight col fat="true" pad="true">
          <Flex as="form" col tall onSubmit={handleSubmit}>
            <ConditionalContext.Provider value={conditional}>
              <Match />
              <Divider />
              <Conditionals labelPrefix="validation-conditionals" />
              <Flex right>
                <AddConditionalButton />
              </Flex>
              <Divider />
              <Flex center xSpace bottom>
                <Flex bottom fat="true" right xSpace>
                  <Button
                    variant="primary"
                    disabled={!conditional.valid || isPending}
                    type="submit">
                    Save
                  </Button>
                </Flex>
              </Flex>
            </ConditionalContext.Provider>
          </Flex>
        </Flex>
        <TransitionPreview
          startNode={startNode}
          destinationNode={destinationNode}
        />
      </Flex>
    </Modal>
  );
};

EditValidationNode.propTypes = {
  handleClose: PropTypes.func.isRequired,
  node: PropTypes.shape({
    id: PropTypes.number.isRequired,
    meta: PropTypes.shape({
      stepId: PropTypes.number.isRequired
    }).isRequired,
    base: PropTypes.shape({
      match: PropTypes.string.isRequired,
      conditionals: PropTypes.array.isRequired,
      variables: PropTypes.arrayOf(
        PropTypes.shape({ id: PropTypes.number.isRequired }).isRequired
      ).isRequired,
      metrics: PropTypes.arrayOf(
        PropTypes.shape({ id: PropTypes.number.isRequired }).isRequired
      ).isRequired,
      start: PropTypes.object.isRequired,
      destination: PropTypes.shape({
        id: PropTypes.number.isRequired
      }).isRequired,
      responses: PropTypes.array.isRequired
    })
  }).isRequired
};

export default EditValidationNode;
